import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export function PWAInstallPrompt() {
    const { t } = useI18n();
    const [installEvent, setInstallEvent] = useState<any>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Detect if it's iOS
        const userAgent = window.navigator.userAgent.toLowerCase();
        const ios = /iphone|ipad|ipod/.test(userAgent);
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;

        setIsIOS(ios);

        // For Android/Chrome
        const handler = (e: any) => {
            console.log("PWA: beforeinstallprompt event fired");
            e.preventDefault();
            setInstallEvent(e);

            const dismissed = sessionStorage.getItem("pwa_prompt_dismissed");

            if (!dismissed && !isStandalone) {
                setIsVisible(true);
            }
        };

        // Fallback: If no event fires within 3 seconds, and it's mobile AND not standalone
        // we show it anyway (it will act as a manual instruction guide)
        const timer = setTimeout(() => {
            const dismissed = sessionStorage.getItem("pwa_prompt_dismissed");
            if (!isStandalone && !dismissed && !installEvent) {
                console.log("PWA: Showing fallback prompt (no event detected)");
                setIsVisible(true);
            }
        }, 3000);

        window.addEventListener("beforeinstallprompt", handler);
        return () => {
            window.removeEventListener("beforeinstallprompt", handler);
            clearTimeout(timer);
        };
    }, [installEvent]);

    const handleInstall = async () => {
        if (isIOS) {
            alert(t("pwa.iosInstallInstructions"));
            return;
        }

        if (installEvent) {
            installEvent.prompt();
            const { outcome } = await installEvent.userChoice;
            console.log(`PWA: User response: ${outcome}`);
            setInstallEvent(null);
            setIsVisible(false);
        } else {
            // Android fallback instructions
            alert(t("pwa.androidInstallInstructions"));
        }
    };

    const handleDismiss = () => {
        sessionStorage.setItem("pwa_prompt_dismissed", "true");
        setIsVisible(false);
    };

    // Only render if visible
    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 bg-primary text-primary-foreground p-4 rounded-xl shadow-2xl z-[9999] animate-in slide-in-from-bottom-10 duration-500 lg:hidden block">
            <div className="flex items-start gap-3">
                <div className="bg-white/20 p-2 rounded-lg shrink-0">
                    <Download className="h-6 w-6" />
                </div>
                <div className="flex-1">
                    <h3 className="font-bold text-sm">{t("pwa.title")}</h3>
                    <p className="text-xs opacity-90 mt-1">
                        {t("pwa.desc")}
                    </p>
                    <div className="flex gap-2 mt-3">
                        <Button
                            onClick={handleInstall}
                            variant="secondary"
                            size="sm"
                            className="w-full text-xs font-bold"
                        >
                            {t("pwa.installNow")}
                        </Button>
                        <Button
                            onClick={handleDismiss}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-white/10"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
